import React from 'react';
import {Checkbox, Modal} from '@carbon/react';

export interface TableHeader {
    key: string;
    header: string;
    type?: string;
    group?: string;
    precision?: number;
    defaultVisible?: boolean;
}

interface CustomizeTableModalProps {
    open: boolean;
    onRequestClose: () => void;
    onSave: () => void;
    onCancel: () => void;
    tableHeaders: TableHeader[];
    pendingColumns: string[];
    onColumnsChange: (columns: string[]) => void;
}

const CustomizeTableModal: React.FC<CustomizeTableModalProps> = ({
    open,
    onRequestClose,
    tableHeaders,
    pendingColumns,
    onColumnsChange,
}) => {
    // Find all unique groups
    const groupMap: Record<string, TableHeader[]> = {};
    tableHeaders.forEach((h) => {
        if (h.group) {
            if (!groupMap[h.group]) groupMap[h.group] = [];
            groupMap[h.group].push(h);
        }
    });

    return (
        <Modal
            open={open}
            onRequestClose={onRequestClose}
            passiveModal
            modalHeading="Customize Columns"
            size="sm"
        >
            <div style={{maxHeight: 400, overflowY: 'auto'}}>
                {Object.entries(groupMap).map(([groupName, groupCols]) => {
                    const allChecked = groupCols.every((h) => pendingColumns.includes(h.key));
                    const someChecked = groupCols.some((h) => pendingColumns.includes(h.key));
                    return (
                        <div key={groupName}>
                            <div
                                className="flex items-center mb-2 mr-4 border border-gray-200 rounded p-2 cursor-pointer hover:bg-gray-100 bg-gray-50 font-semibold"
                                data-testid={`category-checkbox-${groupName.toLowerCase().replace(/\s+/g, '-')}`}
                                onClick={() => {
                                    const cols = [...pendingColumns];
                                    const groupKeys = groupCols.map((h) => h.key);
                                    if (groupKeys.every((k) => cols.includes(k))) {
                                        onColumnsChange(cols.filter((k) => !groupKeys.includes(k)));
                                    } else {
                                        const newCols = [...cols];
                                        groupKeys.forEach((k) => {
                                            if (!newCols.includes(k)) newCols.push(k);
                                        });
                                        onColumnsChange(newCols);
                                    }
                                }}
                            >
                                <Checkbox
                                    id={`col-checkbox-group-${groupName}`}
                                    labelText={groupName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                                    checked={allChecked}
                                    indeterminate={!allChecked && someChecked}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(_, {checked}) => {
                                        const groupKeys = groupCols.map((h) => h.key);
                                        if (checked) {
                                            onColumnsChange(pendingColumns.filter((k) => !groupKeys.includes(k)));
                                        } else {
                                            const newCols = [...pendingColumns];
                                            groupKeys.forEach((k) => {
                                                if (!newCols.includes(k)) newCols.push(k);
                                            });
                                            onColumnsChange(newCols);
                                        }
                                    }}
                                />
                                <span className="ml-2">
                                    {groupName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                                </span>
                            </div>
                            {groupCols.map((header) => (
                                <div
                                    key={header.key}
                                    className="flex items-center mb-2 mr-4 border border-gray-200 rounded p-2 cursor-pointer hover:bg-gray-100 ml-6"
                                    onClick={(e) => {
                                        if ((e.target as HTMLElement).tagName === 'INPUT') return;
                                        onColumnsChange(
                                            pendingColumns.includes(header.key)
                                                ? pendingColumns.filter((k) => k !== header.key)
                                                : [...pendingColumns, header.key]
                                        );
                                    }}
                                >
                                    <Checkbox
                                        id={`col-checkbox-${header.key}`}
                                        labelText={header.header}
                                        checked={pendingColumns.includes(header.key)}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e, {checked}) => {
                                            e.stopPropagation();
                                            onColumnsChange(
                                                checked
                                                    ? pendingColumns.filter((k) => k !== header.key)
                                                    : [...pendingColumns, header.key]
                                            );
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    );
                })}
                {tableHeaders.filter((h) => !h.group).map((header) => (
                    <div
                        key={header.key}
                        className="flex items-center mb-2 mr-4 border border-gray-200 rounded p-2 cursor-pointer hover:bg-gray-100"
                        onClick={(e) => {
                            if ((e.target as HTMLElement).tagName === 'INPUT') return;
                            onColumnsChange(
                                pendingColumns.includes(header.key)
                                    ? pendingColumns.filter((k) => k !== header.key)
                                    : [...pendingColumns, header.key]
                            );
                        }}
                    >
                        <Checkbox
                            id={`col-checkbox-${header.key}`}
                            labelText={header.header}
                            checked={pendingColumns.includes(header.key)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e, {checked}) => {
                                e.stopPropagation();
                                onColumnsChange(
                                    checked
                                        ? pendingColumns.filter((k) => k !== header.key)
                                        : [...pendingColumns, header.key]
                                );
                            }}
                        />
                    </div>
                ))}
            </div>
        </Modal>
    );
};

export default CustomizeTableModal;